import React from 'react';
import { useTranslation } from 'react-i18next';
import GripIcon from '../../../../components/Icons/GripIcon';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ShortEditableContent from '../../../../components/KaTeXComponent/ShortEditableContent';

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';

// One answer option of a QuestionCard. `error`: the translation key of its own error (an empty option);
// `invalidCorrect`: the save found no correct option, so the unmarked ticks are outlined in red.
// Phones (under md): the text takes the whole row and its two buttons (44px) go under it, the tick with its label
// ("Correct answer"); from md up the tick and the remove button are small icons at the end of the row.
export default function Option({
  answer,
  handleOptionUpdate,
  isDeletable,
  removeOption,
  error,
  invalidCorrect = false,
}) {
  const [t] = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: answer.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  // The tick is green once pressed; red-outlined while the save waits for a correct option. Its label reads in
  // slate-600 / red-600 on phones; the bare icon keeps its lighter tone from md up.
  const tickTone = answer.is_correct
    ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/25 hover:bg-emerald-700'
    : invalidCorrect
    ? 'bg-white text-red-600 ring-1 ring-inset ring-red-300 hover:bg-emerald-50 hover:text-emerald-700 hover:ring-emerald-300 md:text-red-400'
    : 'bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-emerald-50 hover:text-emerald-700 hover:ring-emerald-300 md:text-slate-400';
  return (
    <div ref={setNodeRef} style={style} {...attributes} data-invalid={error ? 'true' : undefined}>
      <div className="flex flex-row items-start gap-2">
        <div {...listeners} className="-ml-1 rounded-md px-1 text-slate-300 transition duration-200 hover:bg-slate-100 hover:text-slate-500">
          <GripIcon className="mt-2.5 h-5 cursor-grab" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 md:flex-row md:items-start md:gap-2">
          <div className="w-full min-w-0">
            <ShortEditableContent
              ariaLabel={t('my_questions.upload.option_label', { option: answer.option })}
              sizeClassName={`min-h-[2.5rem]${error ? ' !border-red-400' : ''}`}
              onChange={(e) => {
                handleOptionUpdate('body', e);
              }}
              value={answer.body}
            />
          </div>
          {/* md:contents: from md up the two buttons sit in the option's row itself. */}
          <div className="flex items-center justify-between gap-2 md:contents">
            {/* Named by its label (shown on phones) and the option's letter. */}
            <button
              type="button"
              aria-pressed={!!answer.is_correct}
              title={t('my_questions.editor.mark_correct')}
              onClick={() => handleOptionUpdate('is_correct', !answer.is_correct)}
              className={`relative inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-sm font-medium transition duration-200 md:mt-1.5 md:grid md:h-7 md:w-7 md:place-items-center md:px-0 ${focusRing} ${tickTone}`}
            >
              <CheckIcon className="h-4 w-4" aria-hidden="true" />
              <span className="md:sr-only">{t('my_questions.editor.mark_correct')}</span>
              <span className="sr-only"> ({answer.option})</span>
            </button>
            {isDeletable && (
              <button
                type="button"
                aria-label={t('my_questions.upload.remove_option', { option: answer.option })}
                title={t('buttons.remove')}
                onClick={() => removeOption()}
                className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg text-slate-400 transition duration-200 hover:bg-red-50 hover:text-red-600 md:mt-1.5 md:h-7 md:w-7 ${focusRing}`}
              >
                <XMarkIcon className="h-5 w-5 md:h-4 md:w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
      {error && (
        <div>
          <span className="pl-[2.25rem] text-xs font-medium text-red-600">{t(error)}</span>
        </div>
      )}
    </div>
  );
}
